import { useState } from 'react';
import type { FormEvent } from 'react';
import { consultation } from '@/mocks/red-packet';

const FORM_ADDR = 'https://readdy.ai/api/form/dajn1m7h3l9b18n2bh7g';

export default function Consultation() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检查
    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    // 必填与格式校验
    const name = String(formData.get('name') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const consent = formData.get('consent');

    if (!name) {
      setStatus('error');
      setErrorMsg('请填写您的姓名');
      return;
    }
    if (!phone) {
      setStatus('error');
      setErrorMsg('请填写您的联系方式');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setStatus('error');
      setErrorMsg('请输入正确的 11 位手机号码');
      return;
    }
    if (!consent) {
      setStatus('error');
      setErrorMsg('请阅读并同意隐私政策');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrorMsg('');

    try {
      const res = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        const msg =
          (parsed?.meta as Record<string, unknown> | undefined)?.message ??
          parsed?.message ??
          (parsed?.meta as Record<string, unknown> | undefined)?.detail;
        setErrorMsg(typeof msg === 'string' && msg ? msg : '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="w-full px-4 md:px-6 py-16 md:py-24 bg-foreground-950 scroll-mt-20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* 左侧：文案 */}
        <div className="w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20">
            <span className="w-2 h-2 rounded-full bg-secondary-400"></span>
            <span className="text-xs md:text-sm text-background-100 tracking-wide whitespace-nowrap">
              {consultation.eyebrow}
            </span>
          </div>
          <h2 className="mt-5 font-heading font-bold text-background-50 text-3xl md:text-4xl leading-snug">
            {consultation.title}
          </h2>
          <p className="mt-5 text-sm md:text-base text-foreground-300 leading-relaxed max-w-xl">
            {consultation.desc}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#consultation-form"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-secondary-500 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-secondary-400"
            >
              {consultation.primaryLabel}
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/contact/business"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-background-50/10"
            >
              {consultation.secondaryLabel}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-300">
                <i className="ri-settings-4-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">灵活配置</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">按目标设定红包规则</div>
            </div>
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-300">
                <i className="ri-send-plane-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">精准发放</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">定向触达目标人群</div>
            </div>
            <div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-300">
                <i className="ri-line-chart-line"></i>
              </div>
              <div className="mt-2 text-sm font-medium text-background-50">数据复盘</div>
              <div className="mt-1 text-xs text-foreground-400 leading-relaxed">实时掌握活动成效</div>
            </div>
          </div>
        </div>

        {/* 右侧：表单 */}
        <div id="consultation-form" className="w-full">
          <div className="p-6 md:p-8 rounded-lg bg-background-50">
            <h3 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
              {consultation.formTitle}
            </h3>
            <p className="mt-2 text-xs text-foreground-500">
              请留下您的需求信息，带 <span className="text-secondary-600">*</span> 为必填项
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
              noValidate
            >
              {/* 蜜罐字段（反垃圾） */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp-guard"
              />

              <div>
                <label htmlFor="rp-name" className="block text-sm text-foreground-700 mb-1.5">
                  姓名 <span className="text-secondary-600">*</span>
                </label>
                <input
                  id="rp-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>

              <div>
                <label htmlFor="rp-organization" className="block text-sm text-foreground-700 mb-1.5">
                  单位名称
                </label>
                <input
                  id="rp-organization"
                  name="organization"
                  type="text"
                  placeholder="请输入单位名称"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>

              <div>
                <label htmlFor="rp-phone" className="block text-sm text-foreground-700 mb-1.5">
                  联系方式 <span className="text-secondary-600">*</span>
                </label>
                <input
                  id="rp-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="请输入手机号码"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>

              <div>
                <label htmlFor="rp-region" className="block text-sm text-foreground-700 mb-1.5">
                  所在地区
                </label>
                <input
                  id="rp-region"
                  name="region"
                  type="text"
                  placeholder="例如：广东省佛山市"
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="rp-type" className="block text-sm text-foreground-700 mb-1.5">
                  需求类型
                </label>
                <select
                  id="rp-type"
                  name="need_type"
                  defaultValue=""
                  className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 cursor-pointer"
                >
                  <option value="" disabled>
                    请选择需求类型
                  </option>
                  <option value="政府惠民促消费">政府惠民促消费</option>
                  <option value="商圈与街区引流">商圈与街区引流</option>
                  <option value="乡村振兴与农特产品推广">乡村振兴与农特产品推广</option>
                  <option value="企业福利与品牌营销">企业福利与品牌营销</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="rp-message" className="block text-sm text-foreground-700 mb-1.5">
                  需求描述
                </label>
                <textarea
                  id="rp-message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="请简要描述您的活动目标与需求（不超过 500 字）"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 resize-none"
                ></textarea>
              </div>

              <div className="sm:col-span-2 flex items-start gap-2">
                <input
                  id="rp-consent"
                  name="consent"
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-background-300 cursor-pointer accent-secondary-600"
                />
                <label htmlFor="rp-consent" className="text-xs text-foreground-500 leading-relaxed cursor-pointer">
                  我已阅读并同意隐私政策，同意华辉数智收集和使用上述信息用于方案咨询与联系
                  <span className="text-secondary-600">*</span>
                </label>
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-md bg-secondary-500 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-secondary-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? '正在提交...' : '预约方案咨询'}
                </button>
              </div>

              {status === 'success' && (
                <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-md bg-secondary-100 border border-secondary-200">
                  <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                  <p className="text-sm text-secondary-900 leading-relaxed">{consultation.success}</p>
                </div>
              )}

              {status === 'error' && (
                <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-md bg-background-100 border border-background-200">
                  <i className="ri-error-warning-line text-foreground-500 mt-0.5"></i>
                  <p className="text-sm text-foreground-700 leading-relaxed">{errorMsg}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}